import {
  AiOnboardingDocumentType,
  AiOnboardingProfessionalType,
  IAiOnboardingDetails,
  IAiOnboardingDraft,
  IAiOnboardingFinalProfileFields,
  IAiOnboardingProfileFields,
  IAiOnboardingSnapshot,
} from '../models/AiOnboardingModels';
import {
  IContactPreferences,
  IProvinces,
  ISoftware,
} from '../models/PharmacistModels';

export type ProfessionalType = AiOnboardingProfessionalType;

export const AI_ONBOARDING_CLIENT_TOOL_PREFIX = 'single_session_';

export type SoftwareExperiencePercentage = 0 | 25 | 50 | 75 | 100;

export type SoftwareExperiencePercentageInput =
  `${SoftwareExperiencePercentage}`;

export interface AiOnboardingProfessionalCapacityOption {
  professionalCapacityId: string;
  description: string;
}

export interface AiCompanionSessionContext {
  email: string;
  firstName: string;
  lastName: string;
  preferredLanguage: string;
  preferredLanguageId: string;
  professionalType: ProfessionalType;
  professionalCapacityOptions?: AiOnboardingProfessionalCapacityOption[];
  softwareOptions?: Array<Pick<ISoftware, 'softwareId' | 'name'>>;
  contactPreferenceOptions?: Array<
    Pick<IContactPreferences, 'contactPreferenceId' | 'name'>
  >;
  provinceOptions?: Array<Pick<IProvinces, 'key' | 'name'>>;
  preferredReplacementDurationOptions?: string[];
}

export interface AiOnboardingCatalogs {
  contactPreferenceOptions: NonNullable<
    AiCompanionSessionContext['contactPreferenceOptions']
  >;
  preferredReplacementDurationOptions: NonNullable<
    AiCompanionSessionContext['preferredReplacementDurationOptions']
  >;
  professionalCapacityOptions: NonNullable<
    AiCompanionSessionContext['professionalCapacityOptions']
  >;
  provinceOptions: NonNullable<AiCompanionSessionContext['provinceOptions']>;
  softwareOptions: NonNullable<AiCompanionSessionContext['softwareOptions']>;
}

export interface WorkflowDynamicVariables {
  [key: string]: string | number | boolean;
  contact_preference_options_json: string;
  email: string;
  full_name: string;
  preferred_language: string;
  preferred_language_id: string;
  preferred_replacement_duration_options_json: string;
  professional_capacity_options_json: string;
  professional_type: ProfessionalType;
  province_options_json: string;
  software_options_json: string;
}

export interface VerifyPharmacistLicenseParameters {
  licenseData: {
    licenseNumber: string;
    firstName?: string;
    lastName?: string;
  };
}

export interface VerifyPharmacistLicenseInput {
  firstName: string;
  lastName: string;
  licenseNumber: string;
}

export interface VerifyPharmacistLicenseResult {
  success: boolean;
  licenseVerified: boolean;
  nameVerified: boolean;
}

/**
 * Where the onboarding workflow goes after a licence verification. The agent
 * copies this value into a dynamic variable and routes on it with expression
 * edges, so the values must stay in sync with the ElevenLabs workflow.
 */
export enum LicenseVerificationRoute {
  Continue = 'continue',
  Retry = 'retry',
}

export type VerifyPharmacistLicense = (
  input: VerifyPharmacistLicenseInput,
) => Promise<VerifyPharmacistLicenseResult>;

export interface UpdateProfileNamesParameters {
  profileNames: {
    firstName: string;
    lastName: string;
  };
}

type NullableOnboardingProfileField =
  | 'licenseNumber'
  | 'graduationYear'
  | 'yearsOfExperience'
  | 'availabilityReminder'
  | 'phone'
  | 'preferredDistanceRange'
  | 'latitude'
  | 'longitude'
  | 'diploma'
  | 'instituteName'
  | 'techGraduationYear'
  | 'certificateFileName'
  // #technician-changes: the technician agent never asks for a DSQ key, and
  // only the technician agent asks the four fields below.
  | 'isDSQKeyAvailable'
  | 'isNonSterilCompoundPrepKnowledge'
  | 'handlingHospitalDischargeRating'
  | 'isClickSanteKnowledge'
  | 'isEmpegoKnowledge';

export type SaveOnboardingDetailsFields = Omit<
  IAiOnboardingProfileFields,
  NullableOnboardingProfileField
> & Partial<
  Pick<IAiOnboardingProfileFields, NullableOnboardingProfileField>
>;

export type TrustedOnboardingProfileFields = Pick<
SaveOnboardingDetailsFields,
'firstName' | 'lastName' | 'preferdLanguage' | 'role'
> & {
  catalogs?: AiOnboardingCatalogs;
};

export interface SaveOnboardingDetailsParameters {
  sectionName: 'onboardingDetails';
  fields: SaveOnboardingDetailsFields;
  remainingSoftwarePercentage?: SoftwareExperiencePercentageInput;
}

/**
 * The parts of the professional profile that the onboarding workflow saves one
 * at a time, each through its own client tool. The last part completes the
 * profile, so the order must stay in sync with the ElevenLabs workflow.
 */
export enum OnboardingProfileSection {
  ContactAndExperience = 'contactAndExperience',
  Qualifications = 'qualifications',
  Availability = 'availability',
}

export type OnboardingProfileSectionFields = Partial<SaveOnboardingDetailsFields>;

export interface SaveOnboardingProfileSectionParameters {
  fields: OnboardingProfileSectionFields;
  remainingSoftwarePercentage?: SoftwareExperiencePercentageInput;
}

export interface UploadProfilePictureParameters {
  profilePictureData?: {
    openUpload: boolean;
  };
}

export interface HandleOptionalResumeParameters {
  resumeData: {
    shouldUpload: boolean;
  };
}

export interface UploadTechnicianDiplomaParameters {
  diplomaData: {
    openUpload: boolean;
  };
}

export interface AiOnboardingDocumentUploadResult {
  uploaded: boolean;
}

export type OpenAiOnboardingDocumentUpload = (
  documentType: AiOnboardingDocumentType,
) => Promise<AiOnboardingDocumentUploadResult>;

export enum AiOnboardingSubmissionOutcome {
  Submitted = 'submitted',
  Failed = 'failed',
}

export type SubmitAiOnboardingProfile = (
  profile: IAiOnboardingFinalProfileFields,
) => Promise<void>;

// A profile section is validated before the rest of the profile exists, so the
// validator also receives partial profiles and reports their missing fields.
export type ValidateAiOnboardingProfile = (
  profile: OnboardingProfileSectionFields,
) => Promise<string[]>;

export type LicenseVerificationRecord = Pick<
  IAiOnboardingDetails,
  'licenseNumber'
> & Pick<
  VerifyPharmacistLicenseResult,
  'licenseVerified' | 'nameVerified'
>;

export type OnboardingSnapshot = IAiOnboardingSnapshot;

export interface OnboardingRepository {
  saveLicenseVerification(input: LicenseVerificationRecord): Promise<void>;
  saveOnboardingDetails(input: IAiOnboardingDraft): Promise<void>;
  getSnapshot(): Promise<OnboardingSnapshot>;
}
